import { calmComputed, vueDeepSet } from "../../../../lib/utils";
import { INJECT_UICORE_BUS, INJECT_UICORE_RENDERER } from "../../../../lib/utils/consts";
import { DataStub, mergeDataSchema } from '@tencent/data-schema-core';
import { toPathParts } from '@tencent/data-schema-core/lib/utils/path';
import { formOverridePropsProvider } from "../../../../lib/builtin/plugins/form/formOverrideProps";
import { tryEval } from '../../../utils';
import { cloneDeepDepth } from '../../../utils/cloneDeepDepth';
import { cloneDeep, isEmpty, get } from "lodash-es";
import { getStubProxyRenderer } from "../../../../lib/builtin/components/FormAutoEditor/stubProxyRenderer";
const __script_base__ = {
  name: 'WFormAutoEditor',
  /** 考虑到formautoeditor内部套子组件isFormEditor 需要提供form参数继承 */
  mixins: [formOverridePropsProvider],
  inject: {
    uc: {
      from: INJECT_UICORE_BUS
    },
    renderer: {
      from: INJECT_UICORE_RENDERER
    }
  },
  provide() {
    // 运行时 + 不开启proxySchema 就不考虑proxySchema动态变化的情况了 优化一层proxy
    if (!this.isDesignMode && !this.proxySchema) {
      return {};
    }
    const proxyRenderer = getStubProxyRenderer(this.renderer, () => this.proxySchema && this.schemaType !== 'auto', () => this.path, () => this.dataStub);
    return {
      [INJECT_UICORE_RENDERER]: proxyRenderer
    };
  },
  data() {
    return {
      currentLayout: null
    };
  },
  beforeCreate() {
    this.isDesignMode = !!this.$attrs['data-uc-design-mode'];
  },
  props: {
    path: {
      type: String,
      required: true
    },
    dataSchema: {
      type: Object,
      default: null
    },
    schemaType: {
      type: String,
      default: 'default'
    },
    schemaRule: {
      type: String,
      default: ''
    },
    // 下面两个在某些情况下会变成3态选择框
    required: {
      type: [Boolean, String],
      required: true
    },
    readonly: {
      type: [Boolean, String],
      required: true
    },
    disabled: {
      type: [Boolean, String],
      required: true
    },
    flatten: {
      type: Boolean,
      default: true
    },
    cardInForm: {
      type: Boolean,
      default: true
    },
    proxySchema: {
      type: Boolean,
      default: false
    },
    uiSettings: {
      type: Object,
      default: null
    }
  },
  computed: {
    isBinded() {
      return !!this.path;
    },
    isValid() {
      var _stub$definedFields;
      const stub = this.dataStub;
      if (!stub) return false;
      if (!this.currentLayout) return false;
      if (stub.type === 'object' && !((_stub$definedFields = stub.definedFields) !== null && _stub$definedFields !== void 0 && _stub$definedFields.length)) return false; // 空对象
      return true;
    },
    dataStub: calmComputed(function () {
      var _this$ucRenderer;
      const {
        path
      } = this;
      if (!path) return null;
      const getStubValue = () => get(this.renderer, path);
      if (this.dataSchema && !isEmpty(this.dataSchema) && this.schemaType === 'default') {
        const originStub = this.renderer.getStubAtPath(this.path);
        const mergedSchema = mergeDataSchema(originStub.schema, this.dataSchema);
        if (Array.isArray(mergedSchema === null || mergedSchema === void 0 ? void 0 : mergedSchema.fields)) {
          mergedSchema.fields = mergedSchema.fields.filter(field => {
            var _this$dataSchema;
            return (
              // 如果没有出现在dataSchema的字段 不能出现在表单生成器里
              !!((_this$dataSchema = this.dataSchema) !== null && _this$dataSchema !== void 0 && (_this$dataSchema = _this$dataSchema.fields) !== null && _this$dataSchema !== void 0 && _this$dataSchema.find(item => item.id === field.id))
            );
          });
        }
        return new DataStub({
          value: getStubValue(),
          schema: mergedSchema,
          pathParts: toPathParts(path)
        });
      }
      const rendererStub = (_this$ucRenderer = this.ucRenderer) === null || _this$ucRenderer === void 0 ? void 0 : _this$ucRenderer.getStubAtPath(path);
      if (this.schemaType === 'schemaRule' && this.schemaRule) {
        var _schemaCloned$fields;
        // clone到field
        const schemaCloned = cloneDeepDepth(rendererStub.schema, 2);
        const stubValue = getStubValue();
        (_schemaCloned$fields = schemaCloned.fields) === null || _schemaCloned$fields === void 0 || _schemaCloned$fields.forEach(field => {
          try {
            const renderRuleRes = tryEval(this.schemaRule, this.ucRenderer)({
              get data() {
                return stubValue === null || stubValue === void 0 ? void 0 : stubValue[field.id];
              },
              schema: field
            });
            if (typeof renderRuleRes === 'string') {
              // 可能是7种基本类型
              field.type = renderRuleRes;
            } else if (renderRuleRes) {
              // 只深拷贝ui部分
              field.ui = renderRuleRes;
            }
          } catch (e) {
            console.error('动态编辑控件推导规则错误 key=', field.id, e);
          }
          // patch到schemaCloned里
        });
        // 根据rules来改造schema?
        return new DataStub({
          value: stubValue,
          schema: schemaCloned,
          pathParts: toPathParts(path)
        });
      }
      return rendererStub;
    }),
    // 提供访问 value 的方法，其实好像不一定有用
    value: {
      get() {
        var _this$dataStub;
        return (_this$dataStub = this.dataStub) === null || _this$dataStub === void 0 ? void 0 : _this$dataStub.value;
      },
      set(v) {
        const {
          path,
          renderer
        } = this;
        vueDeepSet(renderer, path, v);
      }
    }
  },
  created() {
    this.$watch(() => (this.uc.components, this.dataStub, this.uiSettings, this.required, this.readonly, this.flatten, this.cardInForm, Symbol()), () => {
      this.currentLayout = this.computeLayout();
    }, {
      immediate: true
    });
  },
  methods: {
    /**
     * 获取当前组件的 Layout
     *
     * @returns {UICore.Layout}
     */
    computeLayout() {
      var _dataStub$schema, _uc$fitData$find;
      /** @type {{
       dataStub: import('@tencent/data-schema-core').DataStub,
       renderer: UICore.CRenderer,
       uc: UICore.UcBus
      }} */
      const {
        dataStub,
        renderer,
        uiSettings,
        uc
      } = this;
      if (!dataStub) return null;
      const cardTitle = !!((_dataStub$schema = dataStub.schema) !== null && _dataStub$schema !== void 0 && _dataStub$schema.title);
      const options = {
        excludeFormItem: true,
        flattenDataSchema: this.flatten,
        cardInForm: this.cardInForm,
        cardTitle
      };
      let ans = (_uc$fitData$find = uc.fitData(dataStub, renderer, options).find(x => x.type === 'editor')) === null || _uc$fitData$find === void 0 ? void 0 : _uc$fitData$find.layout;
      if (!ans) return null;
      ans = cloneDeep({
        ...ans,
        props: {
          ...ans.props,
          ...uiSettings,
          required: this.required,
          readonly: this.readonly,
          disabled: this.disabled
        }
      });
      uc.completeLayout(ans);
      return Object.seal(ans);
    }

    // 无需实现 validate 和 reset 方法，实际的子组件自己会有
  }
};
var render = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return !_vm.isValid ? _c('div', {
    staticClass: "w-formAutoEditor",
    class: {
      isBinded: _vm.isBinded
    }
  }, [!_vm.isBinded && _vm.isDesignMode ? _c('div', {
    staticClass: "uc-inputbox w-formAutoEditor-notBinded"
  }, [_vm._v("\n    请为此 编辑框 绑定数据源\n  ")]) : _vm._e(), _vm._v(" "), _vm.isBinded && _vm.isDesignMode && !_vm.isValid ? _c('div', {
    staticClass: "w-formAutoEditor-invalidInputPlaceholder"
  }, [_vm._v("\n    无效字段组件 " + _vm._s(_vm.path) + "\n  ")]) : _vm._e()]) : _c(_vm.renderer.ucPartialLayoutRenderer, {
    key: "dsField",
    ref: "dsField",
    tag: "component",
    attrs: {
      "layout": _vm.currentLayout
    }
  });
};
var staticRenderFns = [];
const __script__ = __script_base__;
export default Object.assign({
  render,
  staticRenderFns,
  _compiled: true
}, {
  "__file": "builtin/components/FormAutoEditor/index.vue"
}, typeof __script__ === 'function' ? __script__.options : __script__);